import {
  AfterViewInit,
  Component,
  OnInit,
  ElementRef,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import Swiper from 'swiper'; // Import Swiper core
import { Pagination, Navigation, Autoplay } from 'swiper'; // Import necessary modules

import { BreakpointObserver } from '@angular/cdk/layout';
import { APP_CONFIG } from '@core-app/app.config';
import { AuthService, Block, BlockAbstractComponent, ParserService, ZoneService } from '@gallaghercp/core-frontend';

@Component({
  selector: 'core-coro-hero-carousel',
  templateUrl: './coro-hero-carousel.component.html',
  styleUrl: './coro-hero-carousel.component.scss',
  standalone: false,
  // NOTE: Need this otherwise some styles in component SCSS won't apply
  encapsulation: ViewEncapsulation.None,
})
export class CoroHeroCarouselComponent extends BlockAbstractComponent implements OnInit, AfterViewInit, OnDestroy {
  protected override parser: ParserService;
  protected override auth: AuthService;
  protected breakpoint = inject(BreakpointObserver);
  private zoneService = inject(ZoneService);

  // ViewChild to get a reference to the Swiper container in the HTML
  @ViewChild('swiperContainer') swiperContainer!: ElementRef;

  isMobileNav = false;

  // Swiper instance
  private swiper!: Swiper;

  constructor() {
    const parser = inject(ParserService);
    const auth = inject(AuthService);

    super(parser, auth);
    this.parser = parser;
    this.auth = auth;

    // Register Swiper modules globally (important for Swiper 9+)
    // This should ideally be done once in your main app module or a shared module,
    // and will need to consider moving to a global registration if there are multiple Swiper instances.
    Swiper.use([Pagination, Navigation, Autoplay]);
  }

  override ngOnInit() {
    super.ngOnInit();

    this.block.content.map((block: Block) => {
      if (block.data.link && block.data.link.includes('https://')) {
        block.data.targetAttr = 1;
      }
      if (block.data.link && block.data.targetAttr !== 1) {
        block.data.link = this.zoneService.injectZoneCodeUrl(block.data.link);
      }
      return block;
    });

    this.breakpoint.observe(['(max-width: 991px)']).subscribe(result => {
      this.isMobileNav = result.matches;
    });
  }

  ngAfterViewInit(): void {
    // Ensure the swiperContainer is available before initializing Swiper
    if (this.swiperContainer) {
      // Destroy existing swiper if it exists
      if (this.swiper) {
        this.swiper.destroy(true, true);
      }

      // Add a small delay to ensure DOM is ready
      setTimeout(() => {
        this.initializeSwiper();
      }, 1000); // adjust delay here if needed
    }
  }

  getSlideImgUrl(url: string): string {
    if (url && !url.includes('https://')) {
      url = APP_CONFIG.frontend + url;
    }

    return url;
  }

  trackById(index: number, item: Block): number {
    return item?.id ?? index;
  }

  /**
   * Initializes the Swiper instance.
   */
  private initializeSwiper(): void {
    const options = this.block.data.options ?? {}; // Get options from block or initialize as empty object in case of undefined
    const autoplay = options.autoplay
      ? {
          delay: options.intervalSpeed ?? 3000,
          pauseOnMouseEnter: options.pauseOnHover ?? false,
        }
      : false;

    // add additional swiper config options to blockOptions here
    const blockOptions = {
      loop: options.loop ?? false,
      autoplay,
    };

    this.swiper = new Swiper(this.swiperContainer.nativeElement, {
      // If we need pagination
      pagination: {
        el: '.swiper-pagination-home',
        clickable: true, // Make pagination bullets clickable
        bulletActiveClass: 'swiper-pagination-bullet--active',
      },

      // Navigation arrows
      navigation: {
        nextEl: '.swiper-button-hero-carousel-next',
        prevEl: '.swiper-button-hero-carousel-prev',
      },
      spaceBetween: 0,
      slidesPerView: 1,
      // add user-selected blockOptions
      ...blockOptions,
    });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();

    // Destroy Swiper instance to prevent memory leaks
    if (this.swiper) {
      this.swiper.destroy();
    }
  }
}
